import React from 'react'
import cn from 'classnames'
import { omit } from 'lodash'
import {
  Button as BaseButton,
  ButtonProps as BaseButtonProps,
} from '@ariakit/react'
import Loader from '../Icon/Loader'

export const ButtonKinds = {
  APPLE: 'apple',
  CONTROL: 'control',
  FACEBOOK: 'facebook',
  GOOGLE: 'google',
  LINK: 'link',
  LINKED_IN: 'linked-in',
  PLAY: 'play',
  MESSENGER: 'messenger',
  PAYPAL: 'paypal',
  PINTEREST: 'pinterest',
  PRIMARY: 'primary',
  PRIMARY_ACCENT: 'primary-accent',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
  TWITTER: 'twitter',
  UTILITY: 'utility',
} as const

export type ButtonKindValue = (typeof ButtonKinds)[keyof typeof ButtonKinds]

export const ButtonSizes = {
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
  // deprecated
  SMALL: 'small',
  MEDIUM: 'medium',
} as const

export type ButtonSizeValue = (typeof ButtonSizes)[keyof typeof ButtonSizes]

const getTranslatedSize = (size: ButtonSizeValue) => {
  if (!Object.values(ButtonSizes).includes(size)) {
    return ButtonSizes.MD
  }

  switch (size) {
    case ButtonSizes.SMALL:
      return ButtonSizes.SM
    case ButtonSizes.MEDIUM:
      return ButtonSizes.MD
    default:
      return size
  }
}

// Deprecated 'as' prop support
type DeprecatedAsString = 'a' | 'button' | 'div' | 'span' | 'link'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeprecatedAsComponent = React.ComponentType<any>
type DeprecatedAs = DeprecatedAsString | DeprecatedAsComponent

export interface ButtonProps extends Omit<BaseButtonProps, 'as'> {
  /** @deprecated: Reakit props (backward compatibility), use render instead */
  as?: DeprecatedAs
  to?: string
  target?: string
  href?: string | { pathname: string; query?: Record<string, string> }
  /** @deprecated: use accessibleWhenDisabled instead */
  focusable?: boolean

  // Standard props
  css?: Record<string, string>
  fullWidth?: boolean
  kind?: ButtonKindValue
  loading?: boolean
  rounded?: boolean
  size?: ButtonSizeValue
  symmetrical?: boolean
  isBasic?: boolean
  alwaysShowControls?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as,
      to,
      href,
      target,
      children,
      className,
      fullWidth,
      kind = ButtonKinds.PRIMARY,
      loading,
      rounded,
      size = ButtonSizes.MD,
      symmetrical,
      isBasic,
      render,
      focusable,
      ...props
    },
    ref,
  ) => {
    const translatedSize = getTranslatedSize(size)
    const classNames = cn({
      'c-button': true,
      'c-button--full-width': fullWidth,
      'c-button--loading': loading,
      'c-button--symmetrical': symmetrical,
      'c-button--symmetrical mc-corners--circle': rounded,
      [`c-button--${kind}`]: kind,
      [`c-button--${translatedSize}`]: translatedSize,
      [className ?? '']: className,
    })

    const buttonContent = (
      <>
        {!loading && children}
        {loading && (
          <>
            <span className='c-button__content'>{children}</span>
            <Loader className='c-button__loader' />
          </>
        )}
      </>
    )

    // Dev-only deprecation warnings
    // eslint-disable-next-line no-process-env
    if (process.env.NODE_ENV !== 'production') {
      if (as) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Button] Deprecated: `as` prop is deprecated. Use `render` instead.',
        )
      }
      if (focusable) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Button] Deprecated: `focusable` prop is deprecated. Use `accessibleWhenDisabled` instead.',
        )
      }
    }

    // Convert deprecated 'focusable' to 'accessibleWhenDisabled'
    const buttonProps = { ...props }
    if (focusable && !buttonProps.accessibleWhenDisabled) {
      buttonProps.accessibleWhenDisabled = true
    }

    // Handle custom component 'as' prop - bypass Ariakit and render directly.
    // We can't use the render-prop pattern here because Ariakit would inject
    // HTML button props (role, onMouseDown, data-*) into the custom component,
    // which may not accept them. String 'as' stays in Ariakit (see below).
    if (as && typeof as !== 'string' && !render) {
      const Component = as
      const safeButtonProps = omit(buttonProps, 'accessibleWhenDisabled')
      const componentProps: Record<string, unknown> = {
        className: classNames,
        ref,
        isBasic,
        ...safeButtonProps,
      }
      if (to !== undefined) componentProps.to = to
      if (href !== undefined) componentProps.href = href
      if (target !== undefined) componentProps.target = target

      return <Component {...componentProps}>{buttonContent}</Component>
    }

    // Convert deprecated string 'as' to 'render'
    let computedRender = render
    if (as && typeof as === 'string' && !render) {
      if (as === 'a' || as === 'link') {
        // String 'a' → anchor element
        // Only pass href if it's a string (anchor tags don't support object hrefs)
        const anchorProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {
          target,
        }
        if (typeof href === 'string') {
          anchorProps.href = href
        }
        computedRender = (renderProps) => (
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          <a {...renderProps} {...anchorProps} />
        )
      } else {
        // Other strings (div, span, etc)
        computedRender = (renderProps) => React.createElement(as, renderProps)
      }
    }

    return (
      <BaseButton
        className={classNames}
        ref={ref}
        {...(computedRender ? { render: computedRender } : {})}
        {...buttonProps}
      >
        {buttonContent}
      </BaseButton>
    )
  },
)

Button.displayName = 'Button'

export default Button
